<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(10)

// --- Estado Local ---
const grupos = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({
  nome: '',
  descricao: ''
})

// --- Permissões (RBAC) ---
const temPermissaoCompleta = computed(() => authStore.pode('GRUPOS_DE_ACESSO_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('GRUPOS_DE_ACESSO_VISUALIZACAO'))

const podeVerAcoes = computed(() => {
  return temPermissaoCompleta.value || temPermissaoVisualizacao.value
})

const colunas = computed(() => {
  const cols = [
    { field: 'idGrupoAcesso', label: 'ID', width: 100, sortable: true, slotName: 'id' },
    { field: 'nome', label: 'Nome', sortable: true, class: 'fw-medium' },
    { field: 'descricao', label: 'Descrição', sortable: true }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field) => {
  if (!field.startsWith('lst')) return null
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

// --- Watchers Essenciais (Isolamento Multi-Tenant) ---
watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    buscarDados(1)
  } else {
    grupos.value = []
    paginacao.value.TotalCount = 0
  }
})

// --- Métodos ---
const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página para listar os grupos de acesso.'
    grupos.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.organizacaoAtiva) {
      params.append('targetIdOrganizacao', authStore.organizacaoAtiva)
    }

    if (filtros.nome) params.append('nome', filtros.nome)
    if (filtros.descricao) params.append('descricao', filtros.descricao)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/GruposAcesso?${params.toString()}`)

    grupos.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar grupos de acesso:', error)
    erro.value = 'Não foi possível carregar os dados dos grupos de acesso.'
    grupos.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.nome = ''
  filtros.descricao = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarGrupo = () => {
  emit('adicionar-item')
}

const editarGrupo = (grupo) => {
  emit('editar-item', grupo)
}

const excluirGrupo = (grupo) => {
  emit('excluir-item', { ...grupo, name: grupo.nome })
}

// --- Inicialização ---
onMounted(() => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({
  buscarDados
})
</script>

<template>
  <PaginaAdministrar
    titulo="Grupos de Acesso"
    subtitulo="Listagem e gerenciamento de registos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarGrupo"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label for="filtro-nome" class="form-label">Nome</label>
          <input type="text" id="filtro-nome" class="form-control" v-model="filtros.nome" @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label for="filtro-descricao" class="form-label">Descrição</label>
          <input type="text" id="filtro-descricao" class="form-control" v-model="filtros.descricao" @keydown.enter.prevent="aplicarFiltros">
        </div>

        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="grupos"
      :loading="loading"
      empty-message="Nenhum grupo de acesso encontrado."
      row-key="idGrupoAcesso"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarGrupo($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idGrupoAcesso }}</span>
      </template>
      <template #acoes="{ row }">
        <div class="acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-primary" title="Editar" @click="editarGrupo(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
          <button class="btn-icon" title="Excluir" @click="excluirGrupo(row)" v-if="temPermissaoCompleta">
            <i class="bi bi-trash-fill text-danger"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
